import { NumericInput } from "@blueprintjs/core";
import * as ContextMenu from "@radix-ui/react-context-menu";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import { Button, Tooltip } from "antd";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import clsx from "clsx";
import { times } from "lodash";
import React, {
  FocusEvent,
  FunctionComponent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  CellProps,
  Column,
  HeaderProps,
  Row,
  SortingRule,
  TableBodyPropGetter,
  TableBodyProps,
  UseSortByColumnOptions,
  useExpanded,
  useFlexLayout,
  usePagination,
  useRowSelect,
  useRowState,
  useSortBy,
  useTable,
} from "react-table";
import styled from "styled-components";
import tinycolor from "tinycolor2";
import { ReactComponent as ArrowDesc } from "assets/icons/common/arrow-down.svg";
import { ReactComponent as ChevronLeft } from "assets/icons/common/chevron-left-md.svg";
import { ReactComponent as ChevronRight } from "assets/icons/common/chevron-right-dropdown.svg";
import { ReactComponent as ContextMenuIcon } from "assets/icons/common/dotdotdot.svg";
import { usePrevious } from "hooks/ui";
import { useIsKeyboardFocus } from "hooks/ui/useIsKeyboardFocus";
import { scrollbarLightCss } from "legacy/constants/DefaultTheme";
import { Layers } from "legacy/constants/Layers";
import { isPresent } from "legacy/utils/helpers";
import { colors } from "styles/colors";
import { styleAsClass } from "styles/styleAsClass";
import { Checkbox } from "./Checkbox";
import { CollapseSectionButton } from "./CollapseSectionButton";
import SBContextMenu from "./ContextMenu";
import { KeyboardShortcut } from "./KeyboardShortcut";

const TABLE_ROW_HEIGHT = 48;
const TABLE_HEADER_HEIGHT = 40;
export const CELL_PADDING_WIDTH = 16;
export const DEFAULT_TABLE_PAGE_SIZE = 15;
export const ACTION_MENU_WIDTH = 64;
const BUTTON_COLUMN_WIDTH = 16;
const DEFAULT_PAGE_SIZE = 10;

const TableWrapper = styleAsClass`
  border-radius: 4px;
  border: 1px solid ${colors.GREY_100};
`;

// wrapper on table element, execluding pagination element
const TableContentWrapper = styleAsClass`
  ${scrollbarLightCss}
  position: relative;
`;

const TableStyles = styleAsClass`
  width: 100%;
  font-size: 12px;
  .header-cell > .header-cell-inner {
    width: 100%;
    display: flex;
    text-align: left;
    color: ${colors.GREY_700};
    font-weight: 500;
    align-items: center;
    height: ${`${TABLE_HEADER_HEIGHT}px`};
    white-space: nowrap;
  }

  .table-row {
    display: flex;
    width: 100%;
    border: 1px solid transparent;
    border-bottom-color: ${colors.GREY_100};
  }

  .table-cell, .header-cell {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0px ${String(CELL_PADDING_WIDTH)}px;
    white-space: nowrap;
    position: relative;
  }
`;

const SelectedRowStyle = styleAsClass`
  background-color: ${colors.ACCENT_BLUE_500_50};

  &:hover {
    background-color: ${colors.ACCENT_BLUE_500_25};
  }
`;

const ActiveRowStyle = styleAsClass`
  position: relative;
  z-index: 999;
  overflow: visible;
  box-shadow:
    0 0 2px ${colors.ACCENT_BLUE_500},
    0 0 2px inset ${colors.ACCENT_BLUE_500};
  border: 1px solid ${colors.ACCENT_BLUE_500} !important;
`;

const UnclickableRowStyle = styleAsClass`
  cursor: default;
`;

const RowStyle = styleAsClass`
  display: flex;
  flex-direction: row;
  transition: background-color 0.2s ease-in-out;
  height: ${`${TABLE_ROW_HEIGHT}px`};
  user-select: none;

  &:hover {
    background-color: ${colors.GREY_25};
  }

  .action-button-wrapper {
    display: flex;
    gap: 4px;
    justify-content: flex-end;
    width: 100%;
  }

  &:hover .action-button-wrapper button {
    opacity: 1;
  }

  .action-button-wrapper button {
    opacity: 0;
    display: flex;
    padding: 8px;
    justify-content: center;
    align-items: center;
    height: 32px;
    width: 32px;

    border: 1px solid transparent;
    background: transparent;
    box-shadow: 0px 0px 0px;
    color: ${colors.GREY_500};

    &:hover,
    &:focus-visible,
    &[data-state="open"] {
      opacity: 1;
      border-color: ${colors.GREY_100};
      color: ${colors.GREY_500};
      background: ${colors.GREY_25};
    }

    &:active {
      background: ${colors.GREY_50};
    }

    &[data-focused="false"] {
      border-color: "transparent";
    }

    &:disabled {
      border-color: transparent;
      svg {
        color: ${colors.GREY_200};
      }
    }
  }
`;

const HeaderRowStyle = styleAsClass`
  height: ${`${TABLE_HEADER_HEIGHT}px`};
  cursor: default;
`;

const PaginationWrapper = styleAsClass`
  display: flex;
  position: relative;
  height: 48px;
  align-items: center;
  justify-content: center;
`;

const PaginationRecords = styleAsClass`
  font-size: 12px;
  color: ${colors.GREY_500};
  padding-left: 16px;
  position: absolute;
  left: 0px;
`;

const NumericInputStyle = styleAsClass`
  &&& input {
    width: 44px;
    height: 28px;
    padding: 0 !important;
    text-align: center;
    border-width: 0px;
    box-shadow: none;
    border: 1px solid ${colors.GREY_100};
    font-size: 12px;
  }
  margin: 0 8px;
`;

const PageInputWrapper = styleAsClass`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.GREY_500};
  margin: 0 4px;
  white-space: nowrap;
`;

const PageArrow = styleAsClass`
  box-sizing: border-box;
  border-radius: 4px;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 4px;

  cursor: pointer;
  svg {
    width: 18px;
    height: 24px;
  }
  svg path, {
    stroke: ${colors.GREY_500};
  }

  &[data-disabled="true"] {
    pointer-events: none;
    cursor: not-allowed;
    svg path {
      stroke: ${colors.GREY_300};
    }
  }

  &:hover {
    svg path {
      stroke: ${colors.GREY_700};
    }
  }
`;

const EmptyResults = styleAsClass`
  font-size: 12px;
  color: ${colors.GREY_500};
  padding: 16px;
  text-align: center;
  min-height: ${`${TABLE_ROW_HEIGHT}px`};
  border-radius: 4px;
  border-bottom: 1px solid ${colors.GREY_100}
`;

export const TagStyle = styleAsClass`
  height: 24px;
  border-radius: 32px;
  border: 1px solid ${colors.GREY_200};
  display: flex;
  justify-content: center;
  padding: 2px 8px;
  width: fit-content;
  font-weight: 500;
  white-space: nowrap;
`;

const ContextMenuContentClass = styleAsClass`
  display: flex;
  flex-direction: column;
  padding: 8px;
  min-width: 184px;
  max-height: var(--radix-dropdown-menu-content-available-height);
  max-width: minmax(var(--radix-dropdown-menu-content-available-width), 200px);
  overflow: hidden;
  transform-origin: var(--radix-context-menu-content-transform-origin);
  background: white;
  border-radius: 4px;
  box-shadow: 0px 0px 1px 0px rgba(34, 39, 47, 0.32),
    0px 12px 32px -8px rgba(34, 39, 47, 0.16),
    0px 1px 3px 0px rgba(34, 39, 47, 0.12);
  z-index: ${String(Layers.contextMenuContent)};
`;

const ContextMenuSubContentClass = styleAsClass`
  z-index: ${String(Layers.contextMenuSubContent)};
`;

const ContextMenuItemClass = styleAsClass`
  width: 100%;
  padding: 6px 6px 6px 10px;
  display: flex;
  justify-content: space-between;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  line-height: 1.25;
  color: ${colors.GREY_700};
  border-radius: 4px;
  cursor: pointer;

  &[data-highlighted] {
    background-color: ${colors.GREY_50};
  }

  &[data-disabled],
  &[data-highlighted][data-disabled] {
    color: ${colors.GREY_500};
    background-color: transparent;
    cursor: default;
  }
`;

const ContextMenuItemContentClass = styleAsClass`
display: flex;
justify-content: flex-start;
align-items: center;
gap: 12px;
`;

const ContextMenuSubTriggerClass = styleAsClass`
  justify-content: space-between;
`;

const ContextMenuSeparatorClass = styleAsClass`
  height: 1px;
  background-color: ${colors.GREY_100};
  margin: 8px -8px;
`;

export type ActionMenuItem = ItemType & {
  tooltip?: string;
  ["data-test"]?: string;
};

export type QuickActionItem = {
  key: string;
  tooltip: string;
  icon: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const ActionCell = <T extends object>({
  row,
  actionItems,
  dataTestKey,
  disabled,
  uniqueKey,
}: {
  row: ExtendedRow<T>;
  actionItems: RowContextMenuItem<T>[];
  disabled?: boolean;
  uniqueKey?: keyof RowData<T>;
  dataTestKey?: keyof RowData<T>;
}) => {
  const [focused, setFocused] = useState(false);

  const stopPropagation = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  if (actionItems.length === 0) return null;

  return (
    <div
      // make sure we don't open the row's right click context menu when we right click on the button
      // see https://github.com/radix-ui/primitives/blob/main/packages/react/context-menu/src/context-menu.tsx
      onContextMenu={stopPropagation}
      onPointerDown={stopPropagation}
    >
      <RadixDropdown.Root onOpenChange={setFocused}>
        <RadixDropdown.Trigger disabled={disabled} asChild>
          <Button
            title="Options"
            icon={<ContextMenuIcon />}
            onAuxClick={stopPropagation}
            data-focused={focused}
            data-test={`table-action-button-${dataTestKey ? row.original?.[dataTestKey] : uniqueKey ? row.original?.[uniqueKey] : ""}`}
          />
        </RadixDropdown.Trigger>

        <RadixDropdown.Portal css={{ zIndex: Layers.contextMenuContent }}>
          <RadixDropdown.Content
            sideOffset={5}
            align="end"
            className={ContextMenuContentClass}
          >
            {actionItems.map((x) => {
              switch (x.type) {
                case "item":
                  return (
                    <RadixDropdown.Item
                      key={x.key}
                      className={clsx(ContextMenuItemClass, x.className)}
                      data-test={x["data-test"]}
                      disabled={x.disabled}
                      onClick={() => x.onClick?.(row.original)}
                    >
                      <span className={ContextMenuItemContentClass}>
                        {x.icon}
                        {x.label}
                      </span>
                      {x.shortcutKeys && (
                        <KeyboardShortcut shortcutKeys={x.shortcutKeys} />
                      )}
                    </RadixDropdown.Item>
                  );

                case "separator":
                  return (
                    <RadixDropdown.Separator
                      className={ContextMenuSeparatorClass}
                      key={x.key}
                    />
                  );

                case "submenu":
                  return (
                    <RadixDropdown.Sub key={x.key}>
                      <RadixDropdown.SubTrigger
                        className={clsx(
                          ContextMenuItemClass,
                          ContextMenuSubTriggerClass,
                          x.className,
                        )}
                        data-test={x["data-test"]}
                      >
                        {x.label} <ChevronRight aria-hidden focusable={false} />
                      </RadixDropdown.SubTrigger>
                      <RadixDropdown.SubContent
                        className={clsx(
                          ContextMenuContentClass,
                          ContextMenuSubContentClass,
                          x.subContentClassName,
                        )}
                      >
                        {x.items.map((y) => {
                          // we could also make this recursive, but let's just allow a single level of submenus for now
                          switch (y.type) {
                            case "item":
                              return (
                                <RadixDropdown.Item
                                  key={y.key}
                                  className={clsx(
                                    ContextMenuItemClass,
                                    y.className,
                                  )}
                                  data-test={y["data-test"]}
                                  disabled={y.disabled}
                                  onClick={() => y.onClick?.(row.original)}
                                >
                                  <span className={ContextMenuItemContentClass}>
                                    {y.icon}
                                    {y.label}
                                  </span>
                                  {y.shortcutKeys && (
                                    <KeyboardShortcut
                                      shortcutKeys={y.shortcutKeys}
                                    />
                                  )}
                                </RadixDropdown.Item>
                              );

                            case "separator":
                              return (
                                <RadixDropdown.Separator
                                  className={ContextMenuSeparatorClass}
                                  key={y.key}
                                />
                              );

                            default:
                              return null;
                          }
                        })}
                      </RadixDropdown.SubContent>
                    </RadixDropdown.Sub>
                  );
                default:
                  return null;
              }
            })}
          </RadixDropdown.Content>
        </RadixDropdown.Portal>
      </RadixDropdown.Root>
    </div>
  );
};

const QuickActionsCell = <ItemType extends object>({
  quickActionItems,
  rowValue,
  uniqueKey,
  dataTestKey,
  disabled,
}: {
  quickActionItems: (rowValue: RowData<ItemType>) => QuickActionItem[];
  rowValue: RowData<ItemType>;
  uniqueKey?: keyof RowData<ItemType>;
  dataTestKey?: keyof RowData<ItemType>;
  disabled?: boolean;
}) => {
  const items = useMemo(() => {
    const items = quickActionItems?.(rowValue);

    return items.map((item) => {
      return (
        <Tooltip title={item.tooltip} key={item.key}>
          <Button
            icon={item.icon}
            onClick={item.onClick}
            disabled={disabled}
            data-test={`table-quick-action-${item.key}-${
              dataTestKey
                ? rowValue?.[dataTestKey]
                : uniqueKey
                  ? rowValue?.[uniqueKey]
                  : ""
            }`}
          />
        </Tooltip>
      );
    });
  }, [quickActionItems, rowValue, uniqueKey, dataTestKey, disabled]);
  return <>{items}</>;
};

type WithAction = {
  tableAction?: any;
};

type WithChildren = {
  children?: Array<any>;
};

export type WithCustomRender<ItemType extends object> = {
  renderRow?: (row: RowData<ItemType>) => React.ReactNode;
};

export type RowData<T extends object> = T &
  WithAction &
  WithChildren &
  WithCustomRender<T>;

type ExtendedRow<T extends object> = Row<RowData<T>>;

export type RecColumn<ItemType extends object> = Column<ItemType> & {
  canResize?: boolean;
  style?: React.CSSProperties;
  hidden?: boolean;
} & UseSortByColumnOptions<ItemType>;

export type PaginationOptions = {
  pageSize: number;
};

type RowContextMenuBaseItem<T extends object> = {
  type: "item";
  disabled?: boolean;
  icon?: ReactNode;
  key: string;
  label: ReactNode;
  onClick?: (data: RowData<T>) => void;
  "data-test"?: string;
  className?: string;
  shortcutKeys?: string[];
};

type RowContextMenuSeparator = {
  type: "separator";
  key: string;
};

type RowContextMenuSubmenu<T extends object> = {
  type: "submenu";
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  items: (RowContextMenuBaseItem<T> | RowContextMenuSeparator)[];
  "data-test"?: string;
  className?: string;
  subTriggerClassName?: string;
  subContentClassName?: string;
};

export type RowContextMenuItem<T extends object> =
  | RowContextMenuBaseItem<T>
  | RowContextMenuSubmenu<T>
  | RowContextMenuSeparator;

interface TableProps<ItemType extends object> {
  autoResetPage?: boolean;
  canExpandRows?: boolean;
  className?: string;
  columns: RecColumn<RowData<ItemType>>[];
  currentPage?: number;
  data: ItemType[];
  dataLabel?: string; // data label for empty result
  dataTest?: string;
  dataTestKey?: keyof RowData<ItemType>;
  defaultSortBy?: SortingRule<ItemType>[]; // Uncontrolled sortBy. It sets the initial value
  /**
   * This prop changes the number of columns passed into `useTable` and should be regarded as a 'static' prop.
   * It cannot be changed after the table was instantiated. If you absolutely need to change it, make sure to re-render
   * the table entirely by giving it a new key: `<RecommendedTable key={enableMultiSelect ? 'multi' : 'single'} />`.
   */
  enableMultiSelect?: boolean;
  expandedState?: Record<string, boolean>;
  getContextMenuItems?: (
    row: RowData<ItemType>,
  ) => RowContextMenuItem<ItemType>[];
  getRowSelectableTooltip?: (row: RowData<ItemType>) => string;
  hasFixedColumnWidths?: boolean;
  hasKeyboardNavigation?: boolean;
  hideHeader?: boolean;
  loading?: boolean;
  onRowClick?: (row: RowData<ItemType>) => void;
  onRowDoubleClick?: (row: RowData<ItemType>) => void | Promise<void>;
  onRowExpand?: (row: RowData<ItemType>, isExpanded: boolean) => void;
  onSelectRows?: (selectedRows: RowData<ItemType>[]) => void;
  onSortByChange?: (sortBy: SortingRule<ItemType>[]) => void;
  paginationOptions?: PaginationOptions;
  quickActionOptions?: {
    numOfQuickActions: number;
    quickActionItems: (row: RowData<ItemType>) => QuickActionItem[];
  };
  rowClassName?: string;
  rowClickable?: (row: RowData<ItemType>) => boolean;
  rowSelectable?: (row: RowData<ItemType>) => boolean;
  uniqueKey?: keyof RowData<ItemType>;
}

export const getActionMenuWidth = (totalActions: number) => {
  // 32px is the width of each quick action button
  // 4px is the gap between each quick action button
  // 16px is the padding on the left and right of the quick action buttons
  return totalActions * 32 + (totalActions - 1) * 4 + CELL_PADDING_WIDTH * 2;
};

// loading related
// We will show 3 x 3 loading grids if no data is loaded, and if there is previous data, we will show a loading line based on existings cells
const getLoadingAnimatorBg = (bgColorHex: string) => {
  const transparentBg = tinycolor(bgColorHex).setAlpha(0).toString();
  return `linear-gradient(
    270deg,
    ${transparentBg} 0%,
    ${bgColorHex} 52.56%,
    ${transparentBg} 100%
  );`;
};

const LoadingLineWrapper = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
  background: ${colors.WHITE};
  z-index: 10;
`;

const LoadingLine = styled.div`
  position: absolute;
  top: calc(50% - 3px);
  left: 16px;
  height: 6px;
  width: calc(100% - 32px);
  border-radius: 4px;
  background: ${colors.GREY_100};
`;

const LoadingAnimator = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100%;
  width: 100%;
  background: ${getLoadingAnimatorBg(colors.WHITE)};
  z-index: 11;

  background-repeat: no-repeat;
  animation: movingLoader 1500ms infinite;
  animation-timing-function: linear;

  background-size: 35% 100%;
  background-position: -35% 0;
  @keyframes movingLoader {
    to {
      background-position: calc(100% + 35%) 0;
    }
  }
`;

const SelectCheckbox = styleAsClass`
  opacity: 0;
  transition: opacity 0.1s ease-in-out;

  .${HeaderRowStyle} &,
  [role="gridcell"]:hover &,
  .table-row[aria-selected="true"] &,
  &:has(:focus-visible) {
    opacity: 1;
  }
`;

const LoadingCell = ({ loading }: { loading?: boolean }) => {
  if (!loading) return null;
  return (
    <>
      <LoadingLineWrapper>
        <LoadingLine />
      </LoadingLineWrapper>
    </>
  );
};

type SelectAllRowsHeaderProps<T extends object> = HeaderProps<T> & {
  enableMultiSelect?: boolean;
  paginationOptions?: PaginationOptions;
  isSelectable: (row: ExtendedRow<T>) => boolean;
};

const SelectAllRowsHeader = <T extends object>(
  props: SelectAllRowsHeaderProps<T>,
) => {
  const { rows, page, paginationOptions, isSelectable, toggleAllRowsSelected } =
    props;

  const selectablePageRows = page.filter(isSelectable);
  const selectableRows = rows.filter(isSelectable);

  const isPaginated = !!paginationOptions;

  const isAllPageRowsSelected =
    selectablePageRows.length > 0 &&
    selectablePageRows.every((x) => x.isSelected);

  const isAllRowsSelected =
    selectableRows.length > 0 && selectableRows.every((x) => x.isSelected);

  const isSomeRowsSelected =
    selectableRows.length > 0 &&
    selectableRows.some((x) => x.isSelected) &&
    !isAllRowsSelected;

  const tooltipTitle = useMemo(() => {
    if (isPaginated && !isAllPageRowsSelected && !isAllRowsSelected) {
      return "Select all rows on this page";
    }

    if (isAllPageRowsSelected && !isAllRowsSelected) {
      return "Select all rows";
    }

    return "Deselect all rows";
  }, [isAllPageRowsSelected, isAllRowsSelected, isPaginated]);

  if (!props.enableMultiSelect) return null;

  return (
    <Checkbox
      className={SelectCheckbox}
      tooltipTitle={tooltipTitle}
      checked={isAllRowsSelected}
      partialChecked={
        (isPaginated && isAllPageRowsSelected && !isAllRowsSelected) ||
        isSomeRowsSelected
      }
      onClick={() => {
        // if pagination is enabled and all page rows are selected, toggle all rows across all pages
        if (isPaginated && isAllPageRowsSelected && !isAllRowsSelected) {
          for (const row of rows) {
            if (!isSelectable(row)) continue;
            row.toggleRowSelected(true);
          }
          return;
        }

        if (isAllRowsSelected) {
          return toggleAllRowsSelected(false);
        }

        // toggle all rows
        for (const row of isPaginated ? selectablePageRows : selectableRows) {
          if (!isSelectable(row)) continue;
          row.toggleRowSelected(true);
        }
      }}
      dataTest="table-select-all"
    />
  );
};

const getNextIndex = (
  idx: number,
  direction: "up" | "down",
  len: number,
): number => {
  if (direction === "down") {
    return idx + 1 >= len ? idx : idx + 1;
  } else {
    return idx - 1 < 0 ? 0 : idx - 1;
  }
};

const handleRowSelection = <ItemType extends object>(
  activeRow: ExtendedRow<ItemType>,
  nextRow: ExtendedRow<ItemType>,
  isSelectable: (row: ExtendedRow<ItemType>) => boolean,
) => {
  activeRow.setState({ isActive: false });
  nextRow.setState({ isActive: true });

  if (activeRow.isSelected && nextRow.isSelected) {
    activeRow.toggleRowSelected(false);
    return;
  }

  if (activeRow.isSelected && !nextRow.isSelected && isSelectable(nextRow)) {
    nextRow.toggleRowSelected(true);
    return;
  }

  if (!activeRow.isSelected && nextRow.isSelected) {
    return;
  }

  if (!activeRow.isSelected && !nextRow.isSelected) {
    if (isSelectable(activeRow)) activeRow.toggleRowSelected(true);
    if (isSelectable(nextRow)) nextRow.toggleRowSelected(true);
    return;
  }
};

const RecommendedTable = <ItemType extends object>(
  props: TableProps<ItemType>,
) => {
  const {
    autoResetPage = false,
    canExpandRows,
    className,
    columns,
    data,
    dataLabel = "records",
    dataTest,
    dataTestKey,
    defaultSortBy,
    enableMultiSelect,
    expandedState,
    getContextMenuItems,
    getRowSelectableTooltip,
    hasFixedColumnWidths,
    hasKeyboardNavigation,
    hideHeader,
    loading,
    onRowClick,
    onRowDoubleClick,
    onRowExpand,
    onSelectRows,
    onSortByChange,
    paginationOptions,
    quickActionOptions,
    rowClassName,
    rowClickable,
    rowSelectable,
    uniqueKey,
  } = props;

  const [tableRef, isKeyboardFocus] = useIsKeyboardFocus<HTMLTableElement>();

  const isClickable = useCallback(
    (row: ExtendedRow<ItemType>) => {
      if (!rowClickable) return Boolean(onRowClick);
      return !!onRowClick && rowClickable(row.original);
    },
    [onRowClick, rowClickable],
  );

  const isSelectable = useCallback(
    (row: ExtendedRow<ItemType>) => {
      return rowSelectable ? rowSelectable(row.original) : true;
    },
    [rowSelectable],
  );

  const [selectionAnchorRowIndex, setSelectionAnchorRowIndex] =
    useState<number>();

  const columnsToRender = useMemo(() => {
    const compositeColumns: RecColumn<RowData<ItemType>>[] = [...columns];

    if (onSelectRows && enableMultiSelect) {
      compositeColumns.unshift({
        id: "select",
        Header: (props) => (
          <SelectAllRowsHeader
            {...props}
            enableMultiSelect={enableMultiSelect}
            paginationOptions={paginationOptions}
            isSelectable={isSelectable}
          />
        ),
        Cell: ({
          row,
          rows,
          page,
          toggleAllRowsSelected,
        }: CellProps<ItemType>) => (
          <Checkbox
            className={SelectCheckbox}
            disabled={!isSelectable(row)}
            checked={!!row.isSelected}
            tooltipDelay={400}
            tooltipTitle={
              getRowSelectableTooltip?.(row.original) ?? "Select (Space)"
            }
            onClick={(event) => {
              if (enableMultiSelect) {
                const visibleRows = paginationOptions ? page : rows;
                const currentRowIndex = visibleRows.findIndex(
                  (x) => x.original === row.original,
                );

                if (
                  event.shiftKey &&
                  Number.isInteger(selectionAnchorRowIndex)
                ) {
                  toggleAllRowsSelected(false);

                  visibleRows.forEach((x, index) => {
                    if (!isSelectable(x)) return;

                    if (
                      (index >= selectionAnchorRowIndex! &&
                        index <= currentRowIndex) ||
                      (index <= selectionAnchorRowIndex! &&
                        index >= currentRowIndex)
                    ) {
                      x.toggleRowSelected(true);
                    }
                  });

                  return;
                }

                if (!isSelectable(row)) return;

                setSelectionAnchorRowIndex(
                  // if the row is already selected, unset the selection anchor row index
                  row.isSelected ? undefined : currentRowIndex,
                );

                row.toggleRowSelected(!row.isSelected);

                return;
              }

              // we don't support multi-select in this table, so we need to toggle all other rows off
              toggleAllRowsSelected(false);
              if (!row.isSelected) {
                row.toggleRowSelected(true);
              }
            }}
          />
        ),
        width: BUTTON_COLUMN_WIDTH,
        canResize: false,
        style: { justifyContent: "flex-start" },
      });
    }

    if (getContextMenuItems || quickActionOptions?.quickActionItems) {
      const actionMenuWidth = getActionMenuWidth(
        (quickActionOptions?.numOfQuickActions ?? 0) +
          (getContextMenuItems ? 1 : 0),
      );

      compositeColumns.push({
        id: "tableAction",
        disableSortBy: true,
        Header: "",
        Cell: ({ rows, page, row }: CellProps<RowData<ItemType>>) => {
          const hasSelectedRows = (paginationOptions ? page : rows).some(
            (x) => x.isSelected,
          );
          const hasContextMenuItems =
            getContextMenuItems && getContextMenuItems(row.original).length > 0;

          return (
            <div className="action-button-wrapper">
              {quickActionOptions?.quickActionItems && (
                <QuickActionsCell
                  quickActionItems={quickActionOptions.quickActionItems}
                  rowValue={row.original}
                  uniqueKey={uniqueKey}
                  dataTestKey={dataTestKey}
                  disabled={hasSelectedRows}
                />
              )}
              {hasContextMenuItems && (
                <ActionCell
                  row={row}
                  actionItems={getContextMenuItems(row.original)}
                  dataTestKey={dataTestKey}
                  uniqueKey={uniqueKey}
                  disabled={hasSelectedRows}
                />
              )}
            </div>
          );
        },
        width: actionMenuWidth,
        canResize: false,
        style: {
          justifyContent: "flex-end",
        },
      });
    }

    if (canExpandRows && compositeColumns.length > 0) {
      const firstColumn = compositeColumns[enableMultiSelect ? 1 : 0];
      const originalCell =
        "Cell" in firstColumn && firstColumn.Cell
          ? firstColumn.Cell
          : ({ value }: { value: unknown }) => String(value);

      compositeColumns[enableMultiSelect ? 1 : 0] = {
        ...firstColumn,
        Cell: (cellProps: any) => {
          const onExpand = () => {
            cellProps.row.getToggleRowExpandedProps().onClick();
            onRowExpand?.(cellProps.row.original, !cellProps.row.isExpanded);
          };
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              {cellProps.row.canExpand ? (
                <CollapseSectionButton
                  onClick={onExpand}
                  isCollapsed={!cellProps.row.isExpanded}
                />
              ) : (
                <span style={{ width: "16px", marginRight: "5px" }}></span>
              )}
              {typeof originalCell === "function"
                ? (originalCell as FunctionComponent<CellProps<ItemType>>)(
                    cellProps,
                  )
                : originalCell}
            </div>
          );
        },
      } as any;
    }

    return compositeColumns;
  }, [
    columns,
    onSelectRows,
    enableMultiSelect,
    getContextMenuItems,
    quickActionOptions,
    canExpandRows,
    paginationOptions,
    isSelectable,
    uniqueKey,
    dataTestKey,
    getRowSelectableTooltip,
    selectionAnchorRowIndex,
    onRowExpand,
  ]);

  const hiddenColumns = useMemo(
    () =>
      columnsToRender
        .filter((column) => column.hidden)
        .map((column) => column.accessor) ?? [],
    [columnsToRender],
  ) as string[];

  const hooks = useMemo(() => {
    return [
      useSortBy,
      canExpandRows ? useExpanded : null,
      useRowState,
      useFlexLayout,
      usePagination,
      onSelectRows && enableMultiSelect ? useRowSelect : null,
    ].filter(isPresent);
  }, [canExpandRows, onSelectRows, enableMultiSelect]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageOptions,
    nextPage,
    previousPage,
    pageCount,
    gotoPage,
    setPageSize,
    state: { pageIndex, sortBy },
    selectedFlatRows = [],
  } = useTable<RowData<ItemType>>(
    {
      columns: columnsToRender as Column<RowData<ItemType>>[],
      data: data,
      initialState: {
        pageIndex: 0,
        pageSize: paginationOptions?.pageSize ?? DEFAULT_PAGE_SIZE,
        hiddenColumns,
        expanded: expandedState,
        sortBy: defaultSortBy ?? [],
      },
      autoResetExpanded: false,
      // if set to false, it will prevent reset page num when add/remove item (undefined is same as true)
      autoResetPage,
      getSubRows: useCallback(
        (
          row: ItemType &
            WithAction &
            WithChildren &
            WithCustomRender<ItemType>,
        ) => row.children ?? [],
        [],
      ),
      getRowId: useCallback(
        (
          row: ItemType &
            WithAction &
            WithChildren &
            WithCustomRender<ItemType>,
        ) => row[uniqueKey ?? ("id" as keyof ItemType)] as string,
        [uniqueKey],
      ),
    },
    ...hooks,
  );

  useEffect(() => {
    if (!selectedFlatRows || selectedFlatRows.length === 0) return;
    onSelectRows?.(selectedFlatRows.map((row) => row.original));
  }, [onSelectRows, selectedFlatRows]);

  const prevExpandedState = usePrevious(expandedState);
  useEffect(() => {
    if (canExpandRows && expandedState && prevExpandedState !== expandedState) {
      rows.forEach((row) => {
        const id = row.original[
          uniqueKey ?? ("id" as keyof ItemType)
        ] as string;
        const shouldExpand = Boolean(expandedState[id]);
        if (row.canExpand) {
          row.toggleRowExpanded(shouldExpand);
        }
      });
    }
  }, [expandedState, prevExpandedState, rows, uniqueKey, canExpandRows]);

  useEffect(() => {
    // data.length could be 0 that caused some issues.
    setPageSize(paginationOptions?.pageSize ?? DEFAULT_PAGE_SIZE);
  }, [paginationOptions?.pageSize, setPageSize]);

  const [inputPageNo, setInputPageNo] = React.useState<number>(pageIndex + 1);
  useEffect(() => {
    setInputPageNo(pageIndex + 1);
  }, [pageIndex]);

  // if the paginationOptions change, navigate to the first page
  useEffect(() => {
    if (
      paginationOptions?.pageSize &&
      data.length <= pageIndex * paginationOptions.pageSize
    ) {
      gotoPage(0);
    }
  }, [data.length, paginationOptions?.pageSize, pageIndex, gotoPage]);

  // if the data changes, for instance after searching, navigate to the first page
  useEffect(() => {
    if (!paginationOptions) return;
    gotoPage(0);
  }, [data, gotoPage, paginationOptions]);

  useEffect(() => {
    onSortByChange?.(sortBy);
  }, [sortBy, onSortByChange]);

  const tableHeight = paginationOptions
    ? paginationOptions.pageSize * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT + 1
    : undefined;

  const emtpyResultHeight = paginationOptions
    ? paginationOptions.pageSize * TABLE_ROW_HEIGHT
    : undefined;

  const handleTableFocus = useCallback(
    (event: FocusEvent<HTMLTableElement>) => {
      // activate the first row
      if (
        hasKeyboardNavigation &&
        isKeyboardFocus &&
        rows.every((x) => !x.state.isActive)
      ) {
        rows[0]?.setState({ isActive: true });
      }
    },
    [hasKeyboardNavigation, isKeyboardFocus, rows],
  );

  const handleTableBlur = useCallback(() => {
    for (const row of rows) {
      row.setState({ isActive: false });
    }
  }, [rows]);

  const handleTableKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLTableElement>) => {
      if (!hasKeyboardNavigation) return;

      const rowsOrPage = paginationOptions ? page : rows;

      const activeRowIndex = rowsOrPage.findIndex((row) => row.state?.isActive);
      const activeRow = rowsOrPage.at(activeRowIndex);

      if (activeRowIndex === -1 || !activeRow) return;

      // row selection logic for keyboard navigation
      if (enableMultiSelect && onSelectRows && event.shiftKey) {
        const selectedRows = rowsOrPage.filter((row) => row.isSelected);

        switch (event.key) {
          case "ArrowUp":
          case "ArrowDown": {
            // prevent text selection
            event.preventDefault();

            if (selectedRows.length === 0 && isSelectable(activeRow)) {
              // select the active row
              rowsOrPage.at(activeRowIndex)?.toggleRowSelected(true);
              // remain on the active row
              rowsOrPage.at(activeRowIndex)?.setState({ isActive: true });
              return;
            }

            const direction = (
              {
                ArrowDown: "down",
                ArrowUp: "up",
              } as const
            )[event.key];

            const nextRowIndex = getNextIndex(
              activeRowIndex,
              direction,
              rowsOrPage.length,
            );

            const nextRow = rowsOrPage.at(nextRowIndex);

            if (!nextRow) return;

            handleRowSelection(activeRow, nextRow, isSelectable);

            break;
          }
        }
      }

      switch (event.key) {
        case "ArrowDown":
        case "Tab": {
          const nextRowIndex = getNextIndex(
            activeRowIndex,
            "down",
            rowsOrPage.length,
          );

          rowsOrPage.at(activeRowIndex)?.setState({ isActive: false });
          rowsOrPage.at(nextRowIndex)?.setState({ isActive: true });

          break;
        }
        case "ArrowUp": {
          const nextRowIndex = getNextIndex(
            activeRowIndex,
            "up",
            rowsOrPage.length,
          );

          rowsOrPage.at(activeRowIndex)?.setState({ isActive: false });
          rowsOrPage.at(nextRowIndex)?.setState({ isActive: true });

          break;
        }
        // Space
        case " ": {
          event.preventDefault();
          if (onSelectRows && enableMultiSelect && isSelectable(activeRow)) {
            activeRow.toggleRowSelected();
          }
          break;
        }
        default: {
          const itemsWithMatchingShortcutKey = getContextMenuItems?.(
            activeRow.original,
          )?.filter(
            (
              item,
            ): item is RowContextMenuBaseItem<ItemType> & {
              shortcutKeys: string[];
            } =>
              item.type === "item" &&
              !!item.shortcutKeys &&
              item.shortcutKeys?.some(
                (x) => x.toLocaleLowerCase() === event.key.toLocaleLowerCase(),
              ),
          );

          if (!itemsWithMatchingShortcutKey) break;

          for (const item of itemsWithMatchingShortcutKey) {
            const hasRequiredModifiers = [
              "shift",
              "ctrl",
              "alt",
              // macOS command key
              "meta",
              // macOS option key
              "altgraph",
            ].every((modifier) => {
              if (item.shortcutKeys.includes(modifier)) {
                return {
                  alt: event.altKey,
                  altgraph: event.altKey,
                  ctrl: event.ctrlKey,
                  meta: event.metaKey,
                  shift: event.shiftKey,
                }[modifier];
              }

              // default to true if the item does not require modifier keys
              return true;
            });

            if (item.type === "item" && hasRequiredModifiers) {
              item.onClick?.(activeRow.original);
              // only run one item
              break;
            }
          }

          break;
        }
      }
    },
    [
      enableMultiSelect,
      getContextMenuItems,
      hasKeyboardNavigation,
      isSelectable,
      onSelectRows,
      page,
      paginationOptions,
      rows,
    ],
  );

  const handleRowClick = useCallback(
    (row: Row<ItemType>, rows: Row<ItemType>[]) => {
      // don't show active states at all if we don't have keyboard navigation
      if (!hasKeyboardNavigation) return;

      // deactivate all rows
      for (const row of rows) {
        row.setState({
          isActive: false,
        });
      }

      // activate current row
      row.setState({
        isActive: true,
      });
    },
    [hasKeyboardNavigation],
  );

  const renderedRows = useMemo(
    () => (paginationOptions ? page : rows),
    [paginationOptions, page, rows],
  );

  // Call prepareRow on every row before rendering. We don't call this on just the page
  // so that methods like toggleRowSelected are available on every row.
  for (const row of rows) {
    prepareRow(row);
  }

  return (
    <div
      className={clsx("table-wrapper", TableWrapper, className)}
      style={paginationOptions ? undefined : { borderBottom: "0px" }}
      data-test={dataTest ?? `${dataLabel}-table`}
    >
      <div
        className={TableContentWrapper}
        style={tableHeight ? { height: tableHeight } : {}}
      >
        {(data.length > 0 || !loading) && (
          <div
            {...getTableProps()}
            ref={tableRef}
            role="grid"
            className={clsx("table", TableStyles)}
            aria-multiselectable={enableMultiSelect}
            aria-activedescendant={rows.find((row) => row.state?.isActive)?.id}
            onFocus={handleTableFocus}
            onBlur={handleTableBlur}
            onKeyDown={handleTableKeyPress}
            tabIndex={0}
          >
            {!hideHeader && (
              <div className="thead" role="rowgroup">
                {headerGroups.map((headerGroup) => (
                  // key is supplied by headerGroup.getHeaderGroupProps()
                  // eslint-disable-next-line react/jsx-key
                  <div
                    {...headerGroup.getHeaderGroupProps()}
                    className={clsx("table-row", HeaderRowStyle, rowClassName)}
                    role="row"
                  >
                    {headerGroup.headers.map((column) => {
                      const headerProps = column.getHeaderProps(
                        column.getSortByToggleProps({
                          style: {
                            display: "inline-flex",
                            alignItems: "center",
                            height: `${TABLE_HEADER_HEIGHT}px`,
                          },
                        }),
                      );

                      if (hasFixedColumnWidths) {
                        delete headerProps.style?.flex;
                      }

                      return (
                        // key is supplied by column.getHeaderProps()
                        // eslint-disable-next-line react/jsx-key
                        <div
                          className="header-cell"
                          role="columnheader"
                          title="Toggle sort"
                          {...headerProps}
                        >
                          <div className="header-cell-inner">
                            {column.render("Header")}
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <ArrowDesc stroke="#6C7689" />
                              ) : (
                                <ArrowDesc
                                  stroke="#6C7689"
                                  style={{ transform: "rotate(180deg)" }}
                                />
                              )
                            ) : null}
                          </div>
                          <LoadingCell loading={loading} />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            <TableRows
              getContextMenuItems={getContextMenuItems}
              getTableBodyProps={getTableBodyProps}
              hasFixedColumnWidths={hasFixedColumnWidths}
              isClickable={isClickable}
              isLoading={loading}
              onRowClick={handleRowClick}
              onRowDoubleClick={onRowDoubleClick}
              rows={renderedRows}
            />
          </div>
        )}
        {data.length === 0 && !loading && (
          <div
            className={EmptyResults}
            style={{
              height: emtpyResultHeight,
              borderRadius: paginationOptions ? "0px" : "4px",
            }}
            data-test={`empty-results-${dataLabel}`}
          >
            {`No ${dataLabel} found`}
          </div>
        )}
        {data.length === 0 && loading && (
          <div className={`table ${TableStyles}`} role="grid">
            <div className="tbody" role="rowgroup">
              {times(3).map((i) => (
                <div className={`table-row ${RowStyle}`} key={i} role="row">
                  {times(columns.length || 3).map((j) => (
                    <div
                      key={j}
                      className="table-cell"
                      style={{ flex: 1 }}
                      role="gridcell"
                    >
                      <div style={{ width: "100%" }} />
                      <LoadingCell loading={true} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        {loading && <LoadingAnimator />}
      </div>
      {paginationOptions && (
        <div className={PaginationWrapper}>
          <div className={PaginationRecords}>{`${data.length} Records`}</div>
          <div
            className={PageArrow}
            data-disabled={pageIndex === 0 ? "true" : "false"}
            onClick={previousPage}
          >
            <ChevronLeft />
          </div>
          <div className={PageInputWrapper}>
            Page{" "}
            <NumericInput
              className={NumericInputStyle}
              value={inputPageNo}
              min={1}
              max={pageCount || 1}
              buttonPosition="none"
              clampValueOnBlur
              onValueChange={setInputPageNo}
              onBlur={() => gotoPage(inputPageNo - 1)}
            />{" "}
            of {pageCount}
          </div>
          <div
            className={PageArrow}
            data-disabled={
              pageIndex === pageOptions.length - 1 ? "true" : "false"
            }
            onClick={nextPage}
          >
            <ChevronLeft style={{ transform: "rotate(180deg)" }} />
          </div>
        </div>
      )}
    </div>
  );
};

const TableBodyClass = styleAsClass`
  display: flex;
  flex-direction: column;
`;

interface TableRowsProps<T extends object> {
  getContextMenuItems?: (row: RowData<T>) => RowContextMenuItem<T>[];
  getTableBodyProps: (props?: TableBodyPropGetter<T>) => TableBodyProps;
  hasFixedColumnWidths?: boolean;
  isClickable: (row: ExtendedRow<T>) => boolean;
  isLoading?: boolean;
  onRowClick?: (row: ExtendedRow<T>, rows: ExtendedRow<T>[]) => void;
  onRowDoubleClick?: (row: RowData<T>) => void;
  rows: ExtendedRow<T>[];
}

function TableRows<T extends object>(props: TableRowsProps<T>) {
  const {
    getContextMenuItems,
    getTableBodyProps,
    hasFixedColumnWidths,
    isClickable,
    isLoading,
    onRowClick,
    onRowDoubleClick,
    rows,
  } = props;

  const handleRowDoubleClick = useCallback(
    (row: ExtendedRow<T>) => {
      if (!onRowDoubleClick && row.canExpand) {
        row.toggleRowExpanded();
      }

      onRowDoubleClick?.(row.original);
    },
    [onRowDoubleClick],
  );

  const handleRowClick = useCallback(
    (row: ExtendedRow<T>) => onRowClick?.(row, rows),
    [onRowClick, rows],
  );

  // this cannot be memoized or it will not accurately reflect the selection state
  const hasMultipleSelectedRows =
    rows.filter((row) => row.isSelected).length > 1;

  return (
    <div className={TableBodyClass} {...getTableBodyProps()} role="rowgroup">
      {rows.map((row, _, rowsOrPage) => {
        if (row.original.renderRow) {
          return row.original.renderRow(row.original);
        }

        const isRowClickable = isClickable(row);

        return (
          <TableRow
            key={row.id}
            row={row}
            isRowClickable={isRowClickable}
            onRowClick={handleRowClick}
            getContextMenuItems={getContextMenuItems}
            onRowDoubleClick={handleRowDoubleClick}
            hasMultipleSelectedRows={hasMultipleSelectedRows}
          >
            <TableRowCells
              hasFixedColumnWidths={hasFixedColumnWidths}
              cells={row.cells}
              isLoading={isLoading}
            />
          </TableRow>
        );
      })}
    </div>
  );
}

interface RowContextMenuProps<T extends object> {
  children: React.ReactNode;
  contextMenuItems: RowContextMenuItem<T>[];
  row: Row<T>;
}

function RowContextMenu<T extends object>(props: RowContextMenuProps<T>) {
  const { children, contextMenuItems, row } = props;

  const [isOpen, setIsOpen] = useState(false);

  const id = useId();

  return (
    <SBContextMenu
      triggerAsChild
      trigger={children}
      contextMenuId={id}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      {contextMenuItems.map((x) => {
        switch (x.type) {
          case "item":
            return (
              <ContextMenu.Item
                key={x.key}
                onClick={() => x.onClick?.(row.original)}
                className={clsx(ContextMenuItemClass, x.className)}
                data-test={x["data-test"]}
                disabled={x.disabled}
              >
                <span className={ContextMenuItemContentClass}>
                  {x.icon}
                  {x.label}
                </span>
                {x.shortcutKeys && (
                  <KeyboardShortcut shortcutKeys={x.shortcutKeys} />
                )}
              </ContextMenu.Item>
            );
          case "submenu":
            return (
              <ContextMenu.Sub>
                <ContextMenu.SubTrigger
                  className={clsx(
                    ContextMenuItemClass,
                    ContextMenuSubTriggerClass,
                    x.className,
                  )}
                  data-test={x["data-test"]}
                >
                  {x.label} <ChevronRight aria-hidden focusable={false} />
                </ContextMenu.SubTrigger>
                <ContextMenu.Portal>
                  <ContextMenu.SubContent
                    className={clsx(
                      ContextMenuContentClass,
                      ContextMenuSubContentClass,
                      x.subContentClassName,
                    )}
                  >
                    {x.items.map((y) => {
                      switch (y.type) {
                        case "item":
                          return (
                            <ContextMenu.Item
                              key={y.key}
                              className={clsx(
                                ContextMenuItemClass,
                                y.className,
                              )}
                              data-test={y["data-test"]}
                              disabled={y.disabled}
                              onClick={() => y.onClick?.(row.original)}
                            >
                              <span className={ContextMenuItemContentClass}>
                                {y.icon}
                                {y.label}
                              </span>
                              {y.shortcutKeys && (
                                <KeyboardShortcut
                                  shortcutKeys={y.shortcutKeys}
                                />
                              )}
                            </ContextMenu.Item>
                          );
                        case "separator":
                          return (
                            <ContextMenu.Separator
                              key={y.key}
                              className={ContextMenuSeparatorClass}
                            />
                          );
                        default:
                          return null;
                      }
                    })}
                  </ContextMenu.SubContent>
                </ContextMenu.Portal>
              </ContextMenu.Sub>
            );
          case "separator":
            return (
              <ContextMenu.Separator
                className={ContextMenuSeparatorClass}
                key={x.key}
              />
            );
          default:
            return null;
        }
      })}
    </SBContextMenu>
  );
}

interface TableRowProps<T extends object> {
  children?: React.ReactNode;
  className?: string;
  dataTestKey?: keyof RowData<T>;
  getContextMenuItems?: (data: T) => RowContextMenuItem<T>[];
  hasMultipleSelectedRows?: boolean;
  isRowClickable: boolean;
  onRowClick: (row: ExtendedRow<T>) => void;
  onRowDoubleClick?: (row: ExtendedRow<T>) => void;
  row: ExtendedRow<T>;
  uniqueKey?: keyof RowData<T>;
}

function TableRow<T extends object>(props: TableRowProps<T>) {
  const {
    children,
    className,
    dataTestKey,
    getContextMenuItems,
    hasMultipleSelectedRows,
    isRowClickable,
    onRowClick,
    onRowDoubleClick,
    row,
    uniqueKey,
  } = props;

  const rowKey = uniqueKey !== undefined ? row.original?.[uniqueKey] : row.id;

  const rowDataTest =
    dataTestKey !== undefined ? row.original?.[dataTestKey] : rowKey;

  const rowJsx = useMemo(
    () => (
      <div
        role="row"
        className={clsx(
          "table-row",
          RowStyle,
          {
            [UnclickableRowStyle]: !isRowClickable,
            [SelectedRowStyle]: row.isSelected,
            [ActiveRowStyle]: row.state?.isActive,
          },
          className,
        )}
        {...row.getRowProps({
          style: {
            cursor: isRowClickable ? "pointer" : "default",
          },
        })}
        onClick={() => onRowClick(row)}
        onDoubleClick={() => onRowDoubleClick?.(row)}
        aria-selected={row.isSelected}
        data-test={`row-${rowDataTest}`}
        id={row.id}
      >
        {children}
      </div>
    ),
    [
      isRowClickable,
      row,
      className,
      rowDataTest,
      children,
      onRowClick,
      onRowDoubleClick,
    ],
  );

  const contextMenuItems = useMemo(
    () => getContextMenuItems?.(row.original),
    [getContextMenuItems, row.original],
  );

  if (
    !contextMenuItems ||
    contextMenuItems.length === 0 ||
    hasMultipleSelectedRows
  ) {
    return rowJsx;
  }

  return (
    <RowContextMenu contextMenuItems={contextMenuItems} row={row}>
      {rowJsx}
    </RowContextMenu>
  );
}

interface TableRowCellsProps<T extends object> {
  cells: Row<T>["cells"];
  isLoading?: boolean;
  hasFixedColumnWidths?: boolean;
}

function TableRowCells<T extends object>(props: TableRowCellsProps<T>) {
  const { cells, isLoading, hasFixedColumnWidths } = props;
  return (
    <>
      {cells.map((cell) => {
        const cellProps = cell.getCellProps({
          className: "table-cell",
          role: "gridcell",
          style: {
            ...(cell.column as RecColumn<T>).style,
            height: `${TABLE_ROW_HEIGHT - 1}px`,
            display: "inline-flex",
            alignItems: "center",
          },
        });

        if (hasFixedColumnWidths) {
          delete cellProps.style?.flex;
        }

        return (
          // key is supplied by cell.getCellProps()
          // eslint-disable-next-line react/jsx-key
          <div {...cellProps}>
            {cell.render("Cell")}
            <LoadingCell loading={isLoading} />
          </div>
        );
      })}
    </>
  );
}

export default RecommendedTable;
